"use client";

/**
 * ThemeProvider — keeps <html class="dark"> in sync with the resolved
 * mode (user override or time-of-day). The pre-paint script in
 * <head> already applied the correct class before hydration; this
 * provider's job is to KEEP IT correct as the clock advances and to
 * expose a setPreference for a (future) settings toggle.
 *
 * The face lives outside React — the URL force, the stored override and
 * the clock — so the provider reads it as an external store. One interval
 * re-resolves it every minute, so a session left open across the
 * dark-from boundary flips on its own without a reload.
 *
 * This provider owns ONE axis: the face. The palette (`palette.ts`) and
 * density (`density.ts`) axes are their own stores — custom properties and
 * `data-density` on <html> — so light/dark and accent never share a string.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  type Mode,
  type Preference,
  readOverride,
  resolveMode,
  writePreference,
} from "./auto-theme";

interface ThemeContextValue {
  mode: Mode;
  /** "light"/"dark" = explicit user lock; "auto" = follow the clock. */
  preference: Preference;
  setPreference: (pref: Preference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyToDocument(mode: Mode): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (mode === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

interface Face {
  mode: Mode;
  preference: Preference;
}

// On the server the face is light and auto (matches :root); the pre-paint
// script has already stamped the real class before hydration, so the first
// client snapshot changes nothing visible.
const SERVER_FACE: Face = { mode: "light", preference: "auto" };
let lastFace: Face = SERVER_FACE;
const faceListeners = new Set<() => void>();

function readFace(): Face {
  const mode = resolveMode();
  const preference = readOverride() ?? "auto";
  if (mode !== lastFace.mode || preference !== lastFace.preference) {
    lastFace = { mode, preference };
  }
  return lastFace;
}

// A minute is plenty granular for a face that only changes twice a day.
function subscribeToFace(notify: () => void): () => void {
  faceListeners.add(notify);
  const id = window.setInterval(notify, 60_000);
  return () => {
    faceListeners.delete(notify);
    window.clearInterval(id);
  };
}

function setPreference(pref: Preference): void {
  writePreference(pref);
  applyToDocument(resolveMode());
  for (const notify of faceListeners) notify();
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { mode, preference } = useSyncExternalStore(
    subscribeToFace,
    readFace,
    () => SERVER_FACE,
  );

  useEffect(() => {
    applyToDocument(mode);
  }, [mode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, preference, setPreference }),
    [mode, preference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}
